import { Howler } from 'howler';
import { audioManager } from './audio-manager';
import { spatialAudio } from './spatial';
import type { SurfaceType } from './surface-detect';
import { type FootstepSpeedMode, getFootstepGainForSpeed } from './movement-sounds';
import { WEAPONS, type WeaponId } from '../core/config';

export type SoundName =
  // Weapon fire (19)
  | 'ak47_fire'
  | 'm4a4_fire'
  | 'm4a1s_fire'
  | 'awp_fire'
  | 'usp_fire'
  | 'glock_fire'
  | 'p250_fire'
  | 'deagle_fire'
  | 'fiveseven_fire'
  | 'tec9_fire'
  | 'famas_fire'
  | 'galil_fire'
  | 'aug_fire'
  | 'sg553_fire'
  | 'mp9_fire'
  | 'mac10_fire'
  | 'p90_fire'
  | 'nova_fire'
  | 'ssg08_fire'
  // Hit feedback (2)
  | 'hit_confirm'
  | 'headshot_confirm'
  // Movement (9)
  | 'footstep'
  | 'footstep_concrete'
  | 'footstep_metal'
  | 'footstep_wood'
  | 'footstep_sand'
  | 'footstep_tile'
  | 'footstep_gravel'
  | 'jump'
  | 'land'
  // Reload (2 universal + 5 per-class start + 5 per-class end — W8)
  | 'reload_start'
  | 'reload_end'
  | 'pistol_reload_start'
  | 'pistol_reload_end'
  | 'rifle_reload_start'
  | 'rifle_reload_end'
  | 'sniper_reload_start'
  | 'sniper_reload_end'
  | 'smg_reload_start'
  | 'smg_reload_end'
  | 'shotgun_reload_start'
  | 'shotgun_reload_end'
  // Bomb (5)
  | 'bomb_plant'
  | 'bomb_tick'
  | 'bomb_tick_fast'
  | 'bomb_explode'
  | 'bomb_defused'
  // Grenades (8)
  | 'he_explode'
  | 'smoke_pop'
  | 'smoke_hiss'
  | 'flash_explode'
  | 'flash_tinnitus'
  | 'fire_ignite'
  | 'fire_crackle'
  | 'grenade_bounce'
  // Events (3)
  | 'round_start'
  | 'death'
  | 'dry_fire'
  // UI (4)
  | 'radio_beep'
  | 'weapon_switch'
  | 'bolt_action'
  | 'ui_select'
  | 'ui_hover'
  | 'ui_error'
  | 'ui_notification'
  | 'distant_gunfire'
  | 'weapon_inspect'
  | 'pistol_inspect'
  | 'rifle_inspect'
  | 'sniper_inspect'
  | 'smg_inspect'
  | 'shotgun_inspect'
  | 'melee_inspect'
  // Extra (2)
  | 'fire_extinguish'
  | 'grenade_pin';

/**
 * Play the fire sound for a weapon (local player).
 */
export function playWeaponSound(weaponId: string): void {
  audioManager.play(`${weaponId}_fire`);
}

/**
 * Play a footstep sound with reduced volume.
 * Selects a surface-specific sound when a surface type is provided.
 * Falls back to 'footstep' (concrete) for backward compatibility.
 *
 * `mode` scales the base gain via {@link getFootstepGainForSpeed}: creep=0.1,
 * walk=0.5, sprint=1.0. Default is 'walk' so callers that don't yet pass a
 * mode produce roughly the same loudness as before.
 */
export function playFootstep(
  surfaceType?: SurfaceType,
  mode: FootstepSpeedMode = 'walk',
): void {
  const soundName = surfaceType ? `footstep_${surfaceType}` : 'footstep';
  audioManager.play(soundName, getFootstepGainForSpeed(mode));
}

/**
 * Play hit confirmation sound. Headshots get a higher-pitched, more distinct ping.
 */
export function playHitConfirm(headshot: boolean): void {
  audioManager.play(headshot ? 'headshot_confirm' : 'hit_confirm');
}

/** Play bomb plant sound. */
export function playBombPlant(): void {
  audioManager.play('bomb_plant');
}

/** Play bomb tick sound (increases frequency as timer runs down). */
export function playBombTick(urgent: boolean): void {
  audioManager.play(urgent ? 'bomb_tick_fast' : 'bomb_tick');
}

/** Play bomb explosion sound. */
export function playBombExplode(): void {
  audioManager.play('bomb_explode');
}

/** Play bomb defused sound. */
export function playBombDefused(): void {
  audioManager.play('bomb_defused');
}

/** Play weapon switch sound (draw/holster). */
export function playWeaponSwitch(): void {
  audioManager.play('weapon_switch', 0.5);
}

/** Play UI select/confirm sound (e.g. successful buy). */
export function playUISelect(): void {
  audioManager.play('ui_select', 0.4);
}

/** Play UI error sound (e.g. failed buy — insufficient funds). */
export function playUIError(): void {
  audioManager.play('ui_error', 0.5);
}

/** Play bolt action cycling sound (sniper reload). */
export function playBoltAction(): void {
  audioManager.play('bolt_action', 0.5);
}

/**
 * W8: Resolve a class-specific sound name for a weapon, falling back to the
 * universal name when no class variant is registered. Each call probes the
 * audio registry once; the cost is a single Map.has() lookup.
 */
function classVariant(weaponId: WeaponId | undefined, base: string): string {
  if (!weaponId) return base;
  const cfg = WEAPONS[weaponId];
  if (!cfg) return base;
  const candidate = `${cfg.type}_${base}`;
  return audioManager.has(candidate) ? candidate : base;
}

/**
 * W8: Play the reload-start sound, picking a per-weapon-class variant when
 * one is registered (e.g. `rifle_reload_start`), otherwise the universal
 * `reload_start`. Replaces the bare `audioManager.play('reload_start')` call.
 */
export function playReloadStart(weaponId: WeaponId): void {
  audioManager.play(classVariant(weaponId, 'reload_start'));
}

/** W8: Counterpart to {@link playReloadStart} for the magazine-seated cue. */
export function playReloadEnd(weaponId: WeaponId): void {
  audioManager.play(classVariant(weaponId, 'reload_end'));
}

/** W8: Per-weapon-class inspect handle / chamber-check sound. */
export function playWeaponInspect(weaponId: WeaponId): void {
  const cfg = WEAPONS[weaponId];
  const candidate = cfg ? `${cfg.type}_inspect` : '';
  const name = candidate && audioManager.has(candidate) ? candidate : 'weapon_inspect';
  audioManager.play(name, 0.4);
}

/** Play subtle UI hover sound. */
export function playUIHover(): void {
  audioManager.play('ui_hover', 0.2);
}

/** Play muffled distant gunfire. */
export function playDistantGunfire(): void {
  audioManager.play('distant_gunfire', 0.15);
}

/**
 * Update the spatial audio listener to match the camera.
 * Call this every frame from the game loop.
 * Also updates the AudioManager's listener position for distance-based falloff.
 */
export function updateAudioListener(
  posX: number,
  posY: number,
  posZ: number,
  forwardX: number,
  forwardY: number,
  forwardZ: number,
): void {
  spatialAudio.updateListenerPosition(posX, posY, posZ);
  spatialAudio.updateListenerOrientation(forwardX, forwardY, forwardZ, 0, 1, 0);
  audioManager.setListenerPosition(posX, posY, posZ);
  // F-AV-001: Howler positions per-sound calls (`sound.pos(...)`) are computed
  // against the global Howler listener, which defaults to (0,0,0) facing +z.
  // Without these updates, stereo panning is anchored to world origin and
  // every positional sound on dust2 / de_outpost / de_neon comes from the
  // wrong angle once the player moves away from spawn.
  Howler.pos(posX, posY, posZ);
  Howler.orientation(forwardX, forwardY, forwardZ, 0, 1, 0);
}

/**
 * Initialize the full audio system.
 * Call once at game startup (after a user interaction to satisfy autoplay policies).
 */
export function initAudio(): void {
  audioManager.init();
  audioManager.registerGameSounds();
}

/**
 * Resume audio context after user gesture (required by browsers).
 */
export async function resumeAudio(): Promise<void> {
  await spatialAudio.resume();
}

/**
 * Tear down the audio system.
 */
export function disposeAudio(): void {
  audioManager.dispose();
  spatialAudio.dispose();
}
